/**
 * GraphBuilder — Visual graph editor for multi-agent configurations.
 *
 * Uses @xyflow/react (React Flow v12) to render an interactive node-edge
 * canvas. Bidirectional sync: visual changes update YAML, and YAML edits
 * update the canvas.
 */
import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  type Node,
  type Edge,
  type Connection,
  type OnConnect,
  type NodeChange,
  type EdgeChange,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Plus, LayoutGrid, Trash2, Bot, GitBranch, Cpu, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { nodeTypes } from './graph-nodes/CustomNodes';
import { GraphNodePanel } from './GraphNodePanel';
import type { AvailableResources } from '@/services/agentWizardApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Represents one node in the graph YAML config. */
export interface GraphNodeConfig {
  name: string;
  type: string;
  model?: string;
  prompt?: string;
  tools?: string[];
  mcp_servers?: string[];
  function?: string;
  router?: string;
  workers?: Array<{ name: string; agent?: string }>;
  hitl_before?: boolean;
  hitl_after?: boolean;
  structured_output?: string;
}

/** Represents one edge in the graph YAML config. */
export interface GraphEdgeConfig {
  from: string;
  to: string;
  condition?: string;
}

/** Full graph config from YAML. */
export interface GraphConfig {
  nodes: GraphNodeConfig[];
  edges: GraphEdgeConfig[];
}

interface GraphBuilderProps {
  graphConfig: GraphConfig;
  onChange: (graphConfig: GraphConfig) => void;
  resources?: AvailableResources | null;
}

// ---------------------------------------------------------------------------
// Conversion: GraphConfig <-> React Flow nodes/edges
// ---------------------------------------------------------------------------

let nodeIdCounter = 0;

function nextNodeId(): string {
  return `node_${++nodeIdCounter}`;
}

/** Computes a simple auto-layout: nodes in a top-down list. */
function autoLayoutPositions(
  nodeNames: string[],
  startIdx: number,
  endIdx: number,
): Record<string, { x: number; y: number }> {
  const positions: Record<string, { x: number; y: number }> = {};
  const colWidth = 250;
  const rowHeight = 120;

  // Place START at top center
  positions['__START__'] = { x: 300, y: 0 };
  positions['__END__'] = { x: 300, y: (nodeNames.length + 1) * rowHeight };

  // Simple dagre-like layout: arrange nodes in rows
  const cols = Math.max(1, Math.ceil(Math.sqrt(nodeNames.length)));
  nodeNames.forEach((name, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    positions[name] = {
      x: 300 - ((cols - 1) * colWidth) / 2 + col * colWidth,
      y: (row + 1) * rowHeight,
    };
  });

  return positions;
}

export function graphConfigToReactFlow(
  config: GraphConfig,
): { nodes: Node[]; edges: Edge[] } {
  const positions = autoLayoutPositions(
    config.nodes.map((n) => n.name),
    0,
    config.nodes.length,
  );

  const nodes: Node[] = [];

  // START node
  nodes.push({
    id: '__START__',
    type: 'start',
    position: positions['__START__'] ?? { x: 300, y: 0 },
    data: { label: 'START' },
    deletable: false,
  });

  // END node
  nodes.push({
    id: '__END__',
    type: 'end',
    position: positions['__END__'] ?? { x: 300, y: 600 },
    data: { label: 'END' },
    deletable: false,
  });

  // Graph nodes
  config.nodes.forEach((n) => {
    const nodeType = n.type === 'agent' ? 'agent'
      : n.type === 'router' ? 'router'
      : n.type === 'orchestrator' ? 'orchestrator'
      : n.type === 'function' ? 'function'
      : 'agent'; // fallback

    nodes.push({
      id: n.name,
      type: nodeType,
      position: positions[n.name] ?? { x: 300, y: 200 },
      data: {
        label: n.name,
        model: n.model,
        prompt: n.prompt,
        toolCount: n.tools?.length ?? 0,
        functionName: n.function,
        workers: n.workers?.map((w) => w.name),
        // Store full config for the property panel
        nodeConfig: n,
      },
    });
  });

  // Edges
  const edges: Edge[] = config.edges.map((e, i) => ({
    id: `e_${i}`,
    source: e.from === 'START' ? '__START__' : e.from,
    target: e.to === 'END' ? '__END__' : e.to,
    label: e.condition ?? undefined,
    markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
    style: { strokeWidth: 2 },
    labelStyle: { fontSize: 11, fontWeight: 500 },
  }));

  return { nodes, edges };
}

export function reactFlowToGraphConfig(
  nodes: Node[],
  edges: Edge[],
): GraphConfig {
  const graphNodes: GraphNodeConfig[] = [];

  nodes.forEach((n) => {
    // Skip terminal nodes
    if (n.id === '__START__' || n.id === '__END__') return;

    const stored = (n.data?.nodeConfig as GraphNodeConfig) ?? {};
    graphNodes.push({
      name: n.id,
      type: stored.type || n.type || 'agent',
      model: stored.model,
      prompt: stored.prompt,
      tools: stored.tools,
      mcp_servers: stored.mcp_servers,
      function: stored.function,
      router: stored.router,
      workers: stored.workers,
      hitl_before: stored.hitl_before,
      hitl_after: stored.hitl_after,
      structured_output: stored.structured_output,
    });
  });

  const graphEdges: GraphEdgeConfig[] = edges.map((e) => ({
    from: e.source === '__START__' ? 'START' : e.source,
    to: e.target === '__END__' ? 'END' : e.target,
    condition: (e.label as string) || undefined,
  }));

  return { nodes: graphNodes, edges: graphEdges };
}

// ---------------------------------------------------------------------------
// Inner component (needs ReactFlowProvider above it)
// ---------------------------------------------------------------------------

function GraphBuilderInner({ graphConfig, onChange, resources }: GraphBuilderProps) {
  const { fitView } = useReactFlow();
  const initialRef = useRef(false);

  // Convert config to React Flow state
  const initial = useMemo(() => graphConfigToReactFlow(graphConfig), []);
  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges);

  // Selected node for property panel
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  // Fit view on first render
  useEffect(() => {
    if (!initialRef.current) {
      initialRef.current = true;
      setTimeout(() => fitView({ padding: 0.2 }), 100);
    }
  }, [fitView]);

  // Sync external config changes into the canvas
  useEffect(() => {
    if (initialRef.current) {
      const next = graphConfigToReactFlow(graphConfig);
      // Only update if node set changed (avoid position resets during editing)
      const currentNames = new Set(nodes.map((n) => n.id));
      const nextNames = new Set(next.nodes.map((n) => n.id));
      const sameNodes = currentNames.size === nextNames.size &&
        [...currentNames].every((n) => nextNames.has(n));
      if (!sameNodes) {
        setNodes(next.nodes);
        setEdges(next.edges);
      }
    }
  }, [graphConfig]);

  // Propagate changes up
  const emitChange = useCallback(
    (ns: Node[], es: Edge[]) => {
      onChange(reactFlowToGraphConfig(ns, es));
    },
    [onChange],
  );

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes);
      // Defer emitting to avoid stale state
      setTimeout(() => {
        setNodes((currentNodes) => {
          setEdges((currentEdges) => {
            emitChange(currentNodes, currentEdges);
            return currentEdges;
          });
          return currentNodes;
        });
      }, 0);
    },
    [onNodesChange, setNodes, setEdges, emitChange],
  );

  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      onEdgesChange(changes);
      setTimeout(() => {
        setNodes((currentNodes) => {
          setEdges((currentEdges) => {
            emitChange(currentNodes, currentEdges);
            return currentEdges;
          });
          return currentNodes;
        });
      }, 0);
    },
    [onEdgesChange, setNodes, setEdges, emitChange],
  );

  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) => {
        const next = addEdge(
          {
            ...connection,
            markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
            style: { strokeWidth: 2 },
          },
          eds,
        );
        setNodes((ns) => {
          emitChange(ns, next);
          return ns;
        });
        return next;
      });
    },
    [setEdges, setNodes, emitChange],
  );

  // Node selection
  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    if (node.id === '__START__' || node.id === '__END__') {
      setSelectedNodeId(null);
    } else {
      setSelectedNodeId(node.id);
    }
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  // Add new node
  const addNode = useCallback(
    (type: 'agent' | 'router' | 'orchestrator' | 'function') => {
      const id = `${type}_${nextNodeId()}`;
      const newNodeConfig: GraphNodeConfig = {
        name: id,
        type,
      };
      const newNode: Node = {
        id,
        type,
        position: { x: 200 + Math.random() * 200, y: 150 + Math.random() * 200 },
        data: {
          label: id,
          nodeConfig: newNodeConfig,
        },
      };
      setNodes((ns) => {
        const next = [...ns, newNode];
        setEdges((es) => {
          emitChange(next, es);
          return es;
        });
        return next;
      });
      setSelectedNodeId(id);
    },
    [setNodes, setEdges, emitChange],
  );

  // Delete selected node
  const deleteSelectedNode = useCallback(() => {
    if (!selectedNodeId || selectedNodeId === '__START__' || selectedNodeId === '__END__') return;
    setNodes((ns) => {
      const next = ns.filter((n) => n.id !== selectedNodeId);
      setEdges((es) => {
        const nextEdges = es.filter(
          (e) => e.source !== selectedNodeId && e.target !== selectedNodeId,
        );
        emitChange(next, nextEdges);
        return nextEdges;
      });
      return next;
    });
    setSelectedNodeId(null);
  }, [selectedNodeId, setNodes, setEdges, emitChange]);

  // Update node config from property panel
  const updateNodeConfig = useCallback(
    (nodeId: string, updatedConfig: GraphNodeConfig) => {
      setNodes((ns) => {
        const next = ns.map((n) => {
          if (n.id !== nodeId) return n;
          // Update the node's ID if name changed
          const newId = updatedConfig.name || nodeId;
          return {
            ...n,
            id: newId,
            data: {
              ...n.data,
              label: updatedConfig.name,
              model: updatedConfig.model,
              prompt: updatedConfig.prompt,
              toolCount: updatedConfig.tools?.length ?? 0,
              functionName: updatedConfig.function,
              workers: updatedConfig.workers?.map((w) => w.name),
              nodeConfig: updatedConfig,
            },
          };
        });

        // If name changed, update edges too
        if (updatedConfig.name && updatedConfig.name !== nodeId) {
          setEdges((es) => {
            const nextEdges = es.map((e) => ({
              ...e,
              source: e.source === nodeId ? updatedConfig.name : e.source,
              target: e.target === nodeId ? updatedConfig.name : e.target,
            }));
            emitChange(next, nextEdges);
            return nextEdges;
          });
          setSelectedNodeId(updatedConfig.name);
        } else {
          setEdges((es) => {
            emitChange(next, es);
            return es;
          });
        }

        return next;
      });
    },
    [setNodes, setEdges, emitChange],
  );

  // Auto-layout
  const autoLayout = useCallback(() => {
    const nodeNames = nodes
      .filter((n) => n.id !== '__START__' && n.id !== '__END__')
      .map((n) => n.id);
    const positions = autoLayoutPositions(nodeNames, 0, nodeNames.length);

    setNodes((ns) =>
      ns.map((n) => ({
        ...n,
        position: positions[n.id] ?? n.position,
      })),
    );

    setTimeout(() => fitView({ padding: 0.2 }), 50);
  }, [nodes, setNodes, fitView]);

  // Keyboard handler for delete
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNodeId) {
        deleteSelectedNode();
      }
    },
    [selectedNodeId, deleteSelectedNode],
  );

  return (
    <div className="flex h-[500px] border rounded-lg overflow-hidden" onKeyDown={onKeyDown} tabIndex={0}>
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          fitView
          deleteKeyCode={null}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={16} size={1} />
          <Controls showInteractive={false} />
          <MiniMap
            nodeStrokeWidth={2}
            pannable
            zoomable
            className="!bg-background !border-border"
          />

          {/* Toolbar */}
          <Panel position="top-left" className="flex gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="secondary" className="gap-1.5 shadow-sm">
                  <Plus className="h-3.5 w-3.5" />
                  Add Node
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => addNode('agent')} className="gap-2">
                  <Bot className="h-4 w-4 text-blue-500" />
                  Agent Node
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addNode('router')} className="gap-2">
                  <GitBranch className="h-4 w-4 text-amber-500" />
                  Router Node
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addNode('orchestrator')} className="gap-2">
                  <Cpu className="h-4 w-4 text-purple-500" />
                  Orchestrator Node
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => addNode('function')} className="gap-2">
                  <Zap className="h-4 w-4 text-green-500" />
                  Function Node
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button size="sm" variant="secondary" onClick={autoLayout} className="gap-1.5 shadow-sm">
              <LayoutGrid className="h-3.5 w-3.5" />
              Auto Layout
            </Button>

            {selectedNodeId && selectedNodeId !== '__START__' && selectedNodeId !== '__END__' && (
              <Button
                size="sm"
                variant="destructive"
                onClick={deleteSelectedNode}
                className="gap-1.5 shadow-sm"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </Button>
            )}
          </Panel>
        </ReactFlow>
      </div>

      {/* Property panel */}
      {selectedNode && selectedNodeId !== '__START__' && selectedNodeId !== '__END__' && (
        <GraphNodePanel
          nodeId={selectedNodeId!}
          nodeConfig={(selectedNode.data?.nodeConfig as GraphNodeConfig) ?? { name: selectedNodeId!, type: 'agent' }}
          onUpdate={(updated) => updateNodeConfig(selectedNodeId!, updated)}
          onClose={() => setSelectedNodeId(null)}
          resources={resources}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exported wrapper (provides ReactFlowProvider)
// ---------------------------------------------------------------------------

export function GraphBuilder(props: GraphBuilderProps) {
  return (
    <ReactFlowProvider>
      <GraphBuilderInner {...props} />
    </ReactFlowProvider>
  );
}

export default GraphBuilder;
